/**
 * Cameroon Land Market - Centralized Routing & Slug Engine
 * Standardized, human-readable, SEO-friendly URL patterns.
 * 
 * Rules:
 * 1. Lowercase, descriptive URLs using hyphens.
 * 2. Predictable structure: /[category]-for-[sale|rent]/[city]
 * 3. Stable listing identifiers: /property/[title-slug]-[id]
 * 4. Automatic canonicalization and legacy query param redirects.
 */

export const CANONICAL_DOMAIN = 'https://cameroonlandmarket.com';

import { ALL_CAMEROON_TOWNS, normalizeRegionParam } from '../data/cameroonCities';

// Centralized Route Mapping for English and French URLs
export interface LocalizedRouteConfig {
  en: string;
  fr: string;
}

export const LOCALIZED_ROUTES: Record<string, LocalizedRouteConfig> = {
  home: { en: '/en', fr: '/fr' },
  map: { en: '/en/map', fr: '/fr/carte' },
  verifyProperty: { en: '/en/verify-property', fr: '/fr/verifier-propriete' },
  verifyTitreFoncier: { en: '/en/verify-titre-foncier', fr: '/fr/verifier-titre-foncier' },
  verifyLandTitle: { en: '/en/verify-land-title', fr: '/fr/verifier-titre-foncier' },
  propertiesForSale: { en: '/en/properties-for-sale', fr: '/fr/biens-a-vendre' },
  landForSale: { en: '/en/land-for-sale', fr: '/fr/terrains-a-vendre' },
  housesForSale: { en: '/en/houses-for-sale', fr: '/fr/maisons-a-vendre' },
  apartmentsForSale: { en: '/en/apartments-for-sale', fr: '/fr/appartements-a-vendre' },
  commercialForSale: { en: '/en/commercial-for-sale', fr: '/fr/commercial-a-vendre' },
  shopsAndOfficesForSale: { en: '/en/shops-and-offices-for-sale', fr: '/fr/boutiques-et-bureaux-a-vendre' },
  propertiesForRent: { en: '/en/properties-for-rent', fr: '/fr/biens-a-louer' },
  apartmentsForRent: { en: '/en/apartments-for-rent', fr: '/fr/appartements-a-louer' },
  housesForRent: { en: '/en/houses-for-rent', fr: '/fr/maisons-a-louer' },
  studiosForRent: { en: '/en/studios-for-rent', fr: '/fr/studios-a-louer' },
  roomsForRent: { en: '/en/rooms-for-rent', fr: '/fr/chambres-a-louer' },
  landForRent: { en: '/en/land-for-rent', fr: '/fr/terrains-a-louer' },
  commercialForRent: { en: '/en/commercial-for-rent', fr: '/fr/commercial-a-louer' },
  shopsAndOfficesForRent: { en: '/en/shops-and-offices-for-rent', fr: '/fr/boutiques-et-bureaux-a-louer' },
  shortStays: { en: '/en/short-stays', fr: '/fr/sejours-courts' },
  hotels: { en: '/en/hotels', fr: '/fr/hotels' },
  motels: { en: '/en/motels', fr: '/fr/motels' },
  guesthouses: { en: '/en/guesthouses', fr: '/fr/maisons-dhotes' },
  resorts: { en: '/en/resorts', fr: '/fr/residences' },
  servicedApartments: { en: '/en/serviced-apartments', fr: '/fr/appartements-meubles' },
  cities: { en: '/en/cities', fr: '/fr/villes' },
  regions: { en: '/en/regions', fr: '/fr/regions' },
  professionals: { en: '/en/professionals', fr: '/fr/professionnels' },
  property: { en: '/en/property', fr: '/fr/propriete' },
  listing: { en: '/en/property', fr: '/fr/propriete' },
  post: { en: '/en/post-property', fr: '/fr/deposer-annonce' },
  postProperty: { en: '/en/post-property', fr: '/fr/deposer-annonce' },
  about: { en: '/en/about', fr: '/fr/a-propos' },
  faq: { en: '/en/faq', fr: '/fr/faq' },
  contact: { en: '/en/contact', fr: '/fr/contact' },
  privacy: { en: '/en/privacy', fr: '/fr/confidentialite' },
  terms: { en: '/en/terms', fr: '/fr/conditions-utilisation' },
  resources: { en: '/en/resources', fr: '/fr/ressources' },
  pricing: { en: '/en/pricing', fr: '/fr/tarifs' },
  fraudAwareness: { en: '/en/fraud-awareness', fr: '/fr/prevention-fraude' },
  cadastre: { en: '/en/cadastre', fr: '/fr/cadastre' },
  valuation: { en: '/en/valuation', fr: '/fr/estimation' },
  advertise: { en: '/en/advertise', fr: '/fr/publicite' },
  marketTrends: { en: '/en/market-trends', fr: '/fr/tendances-marche' },
  guides: { en: '/en/guides', fr: '/fr/ressources' },
  notaries: { en: '/en/notaries', fr: '/fr/professionnels/notaires' },
  surveyors: { en: '/en/surveyors', fr: '/fr/professionnels/geometres' },
  signIn: { en: '/en/signin', fr: '/fr/connexion' },
  signUp: { en: '/en/signup', fr: '/fr/inscription' },
  forgotPassword: { en: '/en/forgot-password', fr: '/fr/mot-de-passe-oublie' },
  resetPassword: { en: '/en/reset-password', fr: '/fr/reinitialiser-mot-de-passe' },
  dashboard: { en: '/en/dashboard', fr: '/fr/tableau-de-bord' },
  myListings: { en: '/en/my-listings', fr: '/fr/mes-annonces' },
  manageListings: { en: '/en/manage-listings', fr: '/fr/mes-annonces' },
  profile: { en: '/en/profile', fr: '/fr/mon-profil' },
  admin: { en: '/en/admin', fr: '/fr/administration' },
  sitemap: { en: '/en/sitemap', fr: '/fr/plan-du-site' },
  news: { en: '/en/news', fr: '/fr/actualites' },
};

// Property Category to URL Slug mappings
export interface CategoryRouteConfig {
  id: string;
  transaction: 'sale' | 'rent' | 'stay';
  slug: string;
  slugFr: string;
  labelEn: string;
  labelFr: string;
  filterType: string;
}

export const CATEGORY_ROUTES: CategoryRouteConfig[] = [
  // For Sale Categories
  {
    id: 'sale-all',
    transaction: 'sale',
    slug: 'properties-for-sale',
    slugFr: 'biens-a-vendre',
    labelEn: 'Properties for Sale',
    labelFr: 'Biens à Vendre',
    filterType: 'all',
  },
  {
    id: 'sale-all-alias',
    transaction: 'sale',
    slug: 'properties-for-sale',
    slugFr: 'proprietes-a-vendre',
    labelEn: 'Properties for Sale',
    labelFr: 'Propriétés à Vendre',
    filterType: 'all',
  },
  {
    id: 'sale-land',
    transaction: 'sale',
    slug: 'land-for-sale',
    slugFr: 'terrains-a-vendre',
    labelEn: 'Land for Sale',
    labelFr: 'Terrains à Vendre',
    filterType: 'land',
  },
  {
    id: 'sale-house',
    transaction: 'sale',
    slug: 'houses-for-sale',
    slugFr: 'maisons-a-vendre',
    labelEn: 'Houses for Sale',
    labelFr: 'Maisons à Vendre',
    filterType: 'house',
  },
  {
    id: 'sale-apartment',
    transaction: 'sale',
    slug: 'apartments-for-sale',
    slugFr: 'appartements-a-vendre',
    labelEn: 'Apartments for Sale',
    labelFr: 'Appartements à Vendre',
    filterType: 'apartment',
  },
  {
    id: 'sale-commercial',
    transaction: 'sale',
    slug: 'commercial-for-sale',
    slugFr: 'commercial-a-vendre',
    labelEn: 'Shops & Offices for Sale',
    labelFr: 'Boutiques & Bureaux à Vendre',
    filterType: 'commercial',
  },
  {
    id: 'sale-shops-and-offices',
    transaction: 'sale',
    slug: 'shops-and-offices-for-sale',
    slugFr: 'boutiques-et-bureaux-a-vendre',
    labelEn: 'Shops & Offices for Sale',
    labelFr: 'Boutiques & Bureaux à Vendre',
    filterType: 'commercial',
  },
  {
    id: 'sale-commercial-property',
    transaction: 'sale',
    slug: 'commercial-property-for-sale',
    slugFr: 'immobilier-commercial-a-vendre',
    labelEn: 'Shops & Offices for Sale',
    labelFr: 'Boutiques & Bureaux à Vendre',
    filterType: 'commercial',
  },

  // For Rent Categories
  {
    id: 'rent-all',
    transaction: 'rent',
    slug: 'properties-for-rent',
    slugFr: 'biens-a-louer',
    labelEn: 'Properties for Rent',
    labelFr: 'Biens à Louer',
    filterType: 'all',
  },
  {
    id: 'rent-all-alias',
    transaction: 'rent',
    slug: 'properties-for-rent',
    slugFr: 'proprietes-a-louer',
    labelEn: 'Properties for Rent',
    labelFr: 'Propriétés à Louer',
    filterType: 'all',
  },
  {
    id: 'rent-apartment',
    transaction: 'rent',
    slug: 'apartments-for-rent',
    slugFr: 'appartements-a-louer',
    labelEn: 'Apartments for Rent',
    labelFr: 'Appartements à Louer',
    filterType: 'apartment',
  },
  {
    id: 'rent-house',
    transaction: 'rent',
    slug: 'houses-for-rent',
    slugFr: 'maisons-a-louer',
    labelEn: 'Houses for Rent',
    labelFr: 'Maisons à Louer',
    filterType: 'house',
  },
  {
    id: 'rent-studio',
    transaction: 'rent',
    slug: 'studios-for-rent',
    slugFr: 'studios-a-louer',
    labelEn: 'Studios for Rent',
    labelFr: 'Studios Meublés à Louer',
    filterType: 'studio',
  },
  {
    id: 'rent-room',
    transaction: 'rent',
    slug: 'rooms-for-rent',
    slugFr: 'chambres-a-louer',
    labelEn: 'Rooms for Rent',
    labelFr: 'Chambres à Louer',
    filterType: 'room',
  },
  {
    id: 'rent-single-room',
    transaction: 'rent',
    slug: 'single-rooms-for-rent',
    slugFr: 'chambres-a-louer',
    labelEn: 'Single Rooms for Rent',
    labelFr: 'Chambres à Louer',
    filterType: 'room',
  },
  {
    id: 'rent-land',
    transaction: 'rent',
    slug: 'land-for-rent',
    slugFr: 'terrains-a-louer',
    labelEn: 'Land for Rent & Lease',
    labelFr: 'Terrains à Louer & Baux',
    filterType: 'land',
  },
  {
    id: 'rent-commercial',
    transaction: 'rent',
    slug: 'commercial-for-rent',
    slugFr: 'commercial-a-louer',
    labelEn: 'Shops & Offices for Rent',
    labelFr: 'Boutiques & Bureaux à Louer',
    filterType: 'commercial',
  },
  {
    id: 'rent-commercial-locaux',
    transaction: 'rent',
    slug: 'commercial-for-rent',
    slugFr: 'locaux-commerciaux-a-louer',
    labelEn: 'Shops & Offices for Rent',
    labelFr: 'Locaux Commerciaux à Louer',
    filterType: 'commercial',
  },
  {
    id: 'rent-shops-and-offices',
    transaction: 'rent',
    slug: 'shops-and-offices-for-rent',
    slugFr: 'boutiques-et-bureaux-a-louer',
    labelEn: 'Shops & Offices for Rent',
    labelFr: 'Boutiques & Bureaux à Louer',
    filterType: 'commercial',
  },

  // Short Stays & Hospitality
  {
    id: 'stay-all',
    transaction: 'stay',
    slug: 'short-stays',
    slugFr: 'sejours-courts',
    labelEn: 'Hotels & Short Stays',
    labelFr: 'Hôtels & Courts Séjours',
    filterType: 'all',
  },
  {
    id: 'stay-all-alias',
    transaction: 'stay',
    slug: 'short-stays',
    slugFr: 'courts-sejours',
    labelEn: 'Hotels & Short Stays',
    labelFr: 'Hôtels & Courts Séjours',
    filterType: 'all',
  },
  {
    id: 'stay-short-stay-apartments',
    transaction: 'stay',
    slug: 'short-stay-apartments',
    slugFr: 'sejours-courts',
    labelEn: 'Short Stay Apartments',
    labelFr: 'Appartements Court Séjour',
    filterType: 'all',
  },
  {
    id: 'stay-hotel',
    transaction: 'stay',
    slug: 'hotels',
    slugFr: 'hotels',
    labelEn: 'Hotels in Cameroon',
    labelFr: 'Hôtels au Cameroun',
    filterType: 'hotel',
  },
  {
    id: 'stay-motel',
    transaction: 'stay',
    slug: 'motels',
    slugFr: 'motels',
    labelEn: 'Motels & Inns',
    labelFr: 'Motels & Auberges',
    filterType: 'motel',
  },
  {
    id: 'stay-guesthouse',
    transaction: 'stay',
    slug: 'guesthouses',
    slugFr: 'maisons-dhotes',
    labelEn: 'Guesthouses & Lodges',
    labelFr: 'Résidences & Lodges',
    filterType: 'guesthouse',
  },
  {
    id: 'stay-guesthouses-lodges',
    transaction: 'stay',
    slug: 'guesthouses-lodges',
    slugFr: 'maisons-dhotes',
    labelEn: 'Guesthouses & Lodges',
    labelFr: 'Résidences & Lodges',
    filterType: 'guesthouse',
  },
  {
    id: 'stay-resort',
    transaction: 'stay',
    slug: 'resorts',
    slugFr: 'residences',
    labelEn: 'Resorts & Holiday Homes',
    labelFr: 'Complexes & Villas Vacances',
    filterType: 'resort',
  },
  {
    id: 'stay-resort-alias',
    transaction: 'stay',
    slug: 'resorts',
    slugFr: 'complexes-hoteliers',
    labelEn: 'Resorts & Holiday Homes',
    labelFr: 'Complexes & Villas Vacances',
    filterType: 'resort',
  },
  {
    id: 'stay-resorts-holiday-homes',
    transaction: 'stay',
    slug: 'resorts-holiday-homes',
    slugFr: 'residences',
    labelEn: 'Resorts & Holiday Homes',
    labelFr: 'Complexes & Villas Vacances',
    filterType: 'resort',
  },
  {
    id: 'stay-serviced',
    transaction: 'stay',
    slug: 'serviced-apartments',
    slugFr: 'appartements-meubles',
    labelEn: 'Serviced Apartments',
    labelFr: 'Appartements avec Services',
    filterType: 'serviced',
  },
  {
    id: 'stay-serviced-alias',
    transaction: 'stay',
    slug: 'serviced-apartments',
    slugFr: 'residences-meublees',
    labelEn: 'Serviced Apartments',
    labelFr: 'Appartements avec Services',
    filterType: 'serviced',
  },
];

// Professional Categories
export const PROFESSIONAL_SLUGS: Record<string, string> = {
  notary: 'notaries',
  surveyor: 'surveyors',
  lawyer: 'lawyers',
  agent: 'agents',
  architect: 'architects',
};

export const PROFESSIONAL_SLUGS_FR: Record<string, string> = {
  notary: 'notaires',
  surveyor: 'geometres',
  lawyer: 'avocats',
  agent: 'agents',
  architect: 'architectes',
};

export const REVERSE_PROFESSIONAL_SLUGS: Record<string, string> = {
  notaries: 'notary',
  surveyors: 'surveyor',
  lawyers: 'lawyer',
  agents: 'agent',
  architects: 'architect',
  // French aliases
  notaires: 'notary',
  geometres: 'surveyor',
  avocats: 'lawyer',
  architectes: 'architect',
};

// Resource Guides Topics
export const RESOURCE_TOPIC_SLUGS: Record<string, string> = {
  'land-guide': 'land-buying-guide',
  cadastre: 'titre-foncier-mindcaf',
  scams: 'anti-scam-checklist',
  diaspora: 'diaspora-investment',
  laws: 'legal-texts-and-laws',
  'land-prices': 'land-prices-douala-yaounde-kribi-buea',
  'buying-costs': 'cost-of-buying-land-and-taxes',
  'customary-land': 'customary-land-vs-titre-foncier',
  'find-surveyor-notary': 'how-to-find-licensed-surveyor-or-notary',
};

export const RESOURCE_TOPIC_SLUGS_FR: Record<string, string> = {
  'land-guide': 'guide-achat-terrain',
  cadastre: 'titre-foncier-mindcaf',
  scams: 'guide-anti-arnaque',
  diaspora: 'investissement-diaspora',
  laws: 'textes-de-loi-fonciers',
  'land-prices': 'prix-terrain-douala-yaounde-kribi-buea',
  'buying-costs': 'frais-achat-terrain-et-taxes',
  'customary-land': 'abandon-coutumier-vs-titre-foncier',
  'find-surveyor-notary': 'trouver-geometre-assermente-ou-notaire',
};

export const REVERSE_RESOURCE_TOPIC_SLUGS: Record<string, string> = {
  'land-buying-guide': 'land-guide',
  'titre-foncier-mindcaf': 'cadastre',
  'anti-scam-checklist': 'scams',
  'diaspora-investment': 'diaspora',
  'legal-texts-and-laws': 'laws',
  'land-prices-douala-yaounde-kribi-buea': 'land-prices',
  'cost-of-buying-land-and-taxes': 'buying-costs',
  'customary-land-vs-titre-foncier': 'customary-land',
  'how-to-find-licensed-surveyor-or-notary': 'find-surveyor-notary',
  // French aliases
  'guide-achat-terrain': 'land-guide',
  'guide-anti-arnaque': 'scams',
  'investissement-diaspora': 'diaspora',
  'textes-de-loi-fonciers': 'laws',
  'prix-terrain-douala-yaounde-kribi-buea': 'land-prices',
  'frais-achat-terrain-et-taxes': 'buying-costs',
  'abandon-coutumier-vs-titre-foncier': 'customary-land',
  'trouver-geometre-assermente-ou-notaire': 'find-surveyor-notary',
};

// Official 10 Regions of Cameroon (Bilingual Static Page Engine)
export interface RegionRouteConfig {
  code: string;
  slug: string;
  slugFr: string;
  nameEn: string;
  nameFr: string;
  capital: string;
  divisionsCount: number;
}

export const REGION_ROUTES: RegionRouteConfig[] = [
  { code: 'CE', slug: 'centre', slugFr: 'centre', nameEn: 'Centre', nameFr: 'Centre', capital: 'Yaoundé', divisionsCount: 10 },
  { code: 'LT', slug: 'littoral', slugFr: 'littoral', nameEn: 'Littoral', nameFr: 'Littoral', capital: 'Douala', divisionsCount: 4 },
  { code: 'SW', slug: 'south-west', slugFr: 'sud-ouest', nameEn: 'South-West', nameFr: 'Sud-Ouest', capital: 'Buea', divisionsCount: 6 },
  { code: 'SU', slug: 'south', slugFr: 'sud', nameEn: 'South', nameFr: 'Sud', capital: 'Ebolowa', divisionsCount: 4 },
  { code: 'OU', slug: 'west', slugFr: 'ouest', nameEn: 'West', nameFr: 'Ouest', capital: 'Bafoussam', divisionsCount: 8 },
  { code: 'NW', slug: 'north-west', slugFr: 'nord-ouest', nameEn: 'North-West', nameFr: 'Nord-Ouest', capital: 'Bamenda', divisionsCount: 7 },
  { code: 'AD', slug: 'adamawa', slugFr: 'adamaoua', nameEn: 'Adamawa', nameFr: 'Adamaoua', capital: 'Ngaoundéré', divisionsCount: 5 },
  { code: 'NO', slug: 'north', slugFr: 'nord', nameEn: 'North', nameFr: 'Nord', capital: 'Garoua', divisionsCount: 4 },
  { code: 'EN', slug: 'far-north', slugFr: 'extreme-nord', nameEn: 'Far North', nameFr: 'Extrême-Nord', capital: 'Maroua', divisionsCount: 6 },
  { code: 'ES', slug: 'east', slugFr: 'est', nameEn: 'East', nameFr: 'Est', capital: 'Bertoua', divisionsCount: 4 },
];

export function getRegionBySlug(slug?: string): RegionRouteConfig | null {
  if (!slug) return null;
  const cleaned = slugify(slug);
  return (
    REGION_ROUTES.find(
      (r) =>
        r.slug === cleaned ||
        r.slugFr === cleaned ||
        slugify(r.nameEn) === cleaned ||
        slugify(r.nameFr) === cleaned ||
        r.code.toLowerCase() === cleaned
    ) || null
  );
}

export function buildRegionUrl(regionSlug: string, language?: 'en' | 'fr'): string {
  const lang = language || getCurrentLanguage();
  const reg = getRegionBySlug(regionSlug);
  if (!reg) {
    const slug = slugify(regionSlug);
    return lang === 'fr' ? `/fr/regions/${slug}` : `/en/regions/${slug}`;
  }
  return lang === 'fr' ? `/fr/regions/${reg.slugFr}` : `/en/regions/${reg.slug}`;
}

export const CAMEROON_REGIONS_CONFIG = REGION_ROUTES;

export interface CentralCityConfig {
  name: string;
  slug: string;
  regionNameEn: string;
  regionNameFr: string;
  regionSlug: string;
  regionSlugFr: string;
  division?: string;
  coords: [number, number];
  popularQuarters: string[];
}

export function getCategoryBySlug(slug?: string): CategoryRouteConfig | null {
  if (!slug) return null;
  const cleaned = slugify(slug);
  return (
    CATEGORY_ROUTES.find(
      (c) => c.slug === cleaned || c.slugFr === cleaned || c.id === cleaned
    ) || null
  );
}

export function getCityBySlug(slug?: string): CentralCityConfig | null {
  if (!slug) return null;
  const cleaned = slugify(slug);
  const town = ALL_CAMEROON_TOWNS.find(
    (t) => t.slug.toLowerCase() === cleaned || slugify(t.name) === cleaned
  );
  if (town) {
    const reg = getRegionBySlug(town.region);
    return {
      name: town.name,
      slug: town.slug || cleaned,
      regionNameEn: reg ? reg.nameEn : town.region,
      regionNameFr: reg ? reg.nameFr : town.region,
      regionSlug: reg ? reg.slug : slugify(town.region),
      regionSlugFr: reg ? reg.slugFr : slugify(town.region),
      division: town.division,
      coords: town.coords || [0, 0],
      popularQuarters: town.popularQuarters || [],
    };
  }
  const trusted = TRUSTED_CAMEROON_CITIES[cleaned];
  if (trusted) {
    const reg = getRegionBySlug(trusted.region);
    return {
      name: trusted.name,
      slug: trusted.slug,
      regionNameEn: reg ? reg.nameEn : trusted.region,
      regionNameFr: reg ? reg.nameFr : trusted.region,
      regionSlug: reg ? reg.slug : slugify(trusted.region),
      regionSlugFr: reg ? reg.slugFr : slugify(trusted.region),
      division: '',
      coords: [0, 0],
      popularQuarters: [],
    };
  }
  return null;
}

export function isCityInRegion(citySlug?: string, regionSlug?: string): boolean {
  if (!citySlug || !regionSlug) return false;
  const city = getCityBySlug(citySlug);
  const reg = getRegionBySlug(regionSlug);
  if (!city || !reg) return false;
  return city.regionSlug === reg.slug;
}

export function buildCategoryRegionUrl(
  categorySlug: string,
  regionSlug: string,
  language?: 'en' | 'fr'
): string {
  const lang = language || getCurrentLanguage();
  const cat = getCategoryBySlug(categorySlug);
  const reg = getRegionBySlug(regionSlug);
  const catSegment = cat ? (lang === 'fr' ? cat.slugFr : cat.slug) : slugify(categorySlug);
  const regSegment = reg ? (lang === 'fr' ? reg.slugFr : reg.slug) : slugify(regionSlug);
  return `/${lang}/${catSegment}/${regSegment}`;
}

export function buildCategoryRegionCityUrl(
  categorySlug: string,
  regionSlug: string,
  citySlug: string,
  language?: 'en' | 'fr'
): string {
  const lang = language || getCurrentLanguage();
  const cat = getCategoryBySlug(categorySlug);
  const reg = getRegionBySlug(regionSlug);
  const city = getCityBySlug(citySlug);
  const catSegment = cat ? (lang === 'fr' ? cat.slugFr : cat.slug) : slugify(categorySlug);
  const regSegment = reg ? (lang === 'fr' ? reg.slugFr : reg.slug) : slugify(regionSlug);
  const citySegment = city ? city.slug : slugify(citySlug);
  return `/${lang}/${catSegment}/${regSegment}/${citySegment}`;
}

/**
 * Robust string slugification (replaces accents, non-alphanumeric, spaces with hyphens)
 */
export function slugify(text: string): string {
  if (!text) return '';
  return text
    .toString()
    .normalize('NFD') // decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // replace non-alphanumeric with hyphen
    .replace(/^-+|-+$/g, ''); // strip leading/trailing hyphens
}

/**
 * Generates a cryptographically secure, random, non-sequential public ID for listings.
 * Pattern: "clm" + 10 lowercase hexadecimal characters (e.g. "clm73a0f895c2", "clm91d82f70ab", "clm64c12e839d").
 */
export function generatePublicId(): string {
  let hex = '';
  if (
    typeof globalThis !== 'undefined' &&
    globalThis.crypto &&
    typeof globalThis.crypto.getRandomValues === 'function'
  ) {
    const bytes = new Uint8Array(5);
    globalThis.crypto.getRandomValues(bytes);
    hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } else {
    // Cryptographically secure fallback
    for (let i = 0; i < 10; i++) {
      hex += Math.floor(Math.random() * 16).toString(16);
    }
  }
  return `clm${hex.toLowerCase()}`;
}

/**
 * Robust string sanitization for URL segments:
 * - Strips script and style tags with content
 * - Strips HTML tags
 * - Converts to lowercase
 * - Strips accents and diacritics
 * - Allows ONLY a-z, 0-9, and hyphens (strips emojis and special symbols)
 * - Collapses spaces, underscores, and hyphens to a single '-'
 * - Removes leading and trailing hyphens
 * - Enforces maximum length
 */
export function sanitizeUrlSegment(text: string, maxLen = 50): string {
  if (!text) return '';
  let cleaned = text
    .toString()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // strip script tags and content
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')   // strip style tags and content
    .replace(/<[^>]*>/g, '') // strip any remaining HTML tags
    .normalize('NFD') // decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-') // allow ONLY lowercase a-z, 0-9, and hyphen
    .replace(/-+/g, '-') // collapse multiple hyphens
    .replace(/^-+|-+$/g, ''); // strip leading/trailing hyphens

  if (cleaned.length > maxLen) {
    cleaned = cleaned.slice(0, maxLen).replace(/-+$/, '');
  }
  return cleaned;
}

/**
 * Normalizes user-input or database property types into canonical URL slugs
 */
export function normalizePropertyTypeSlug(type?: string): string {
  const t = sanitizeUrlSegment(type || '');
  if (t.includes('land') || t.includes('terrain') || t.includes('parcell') || t.includes('lot')) return 'land';
  if (t.includes('house') || t.includes('maison') || t.includes('villa') || t.includes('duplex')) return 'house';
  if (t.includes('apartment') || t.includes('appart') || t.includes('flat') || t.includes('immeuble')) return 'apartment';
  if (t.includes('studio')) return 'studio';
  if (t.includes('hotel') || t.includes('motel') || t.includes('guesthouse') || t.includes('resort') || t.includes('auberge')) return 'hotel';
  if (t.includes('commercial') || t.includes('bureau') || t.includes('office') || t.includes('commerce') || t.includes('shop') || t.includes('boutique') || t.includes('magasin')) return 'commercial';
  return 'property';
}

/**
 * Normalizes transaction types strictly into canonical URL slugs:
 * - 'for-rent' for rental listings
 * - 'for-sale' for sale listings
 */
export function normalizeTransactionTypeSlug(type?: string): string {
  const t = sanitizeUrlSegment(type || '');
  if (
    t === 'rent' ||
    t === 'for-rent' ||
    t === 'location' ||
    t === 'louer' ||
    t === 'short-stay' ||
    t === 'short_stay' ||
    t === 'stay' ||
    t === 'hotel' ||
    t.includes('rent') ||
    t.includes('stay')
  ) {
    return 'for-rent';
  }
  return 'for-sale';
}

/**
 * Protected system slugs that must never be hijacked
 */
export const RESERVED_SLUGS = new Set([
  'admin', 'api', 'auth', 'buy', 'rent', 'stay', 'cities', 'properties-for-sale',
  'properties-for-rent', 'short-stays', 'hotels', 'land-for-sale', 'houses-for-sale',
  'apartments-for-rent', 'studios-for-rent', 'commercial-for-sale', 'commercial-for-rent',
  'shops-and-offices-for-sale', 'shops-and-offices-for-rent',
  'boutiques-et-bureaux-a-vendre', 'boutiques-et-bureaux-a-louer',
  'map', 'verify-property', 'professionals', 'resources', 'about', 'contact', 'faq', 'terms', 'privacy', 'signin', 'signup'
]);

/**
 * Centralized, automatic URL slug generator for property listings.
 * Pattern: {title-slug}-{city-slug}-{id}
 * All components are strictly lowercase.
 * Example:
 * - 600m2-titled-plot-bonamoussadi-douala-1234
 */
export function generatePropertySlug(params: {
  title?: string;
  propertyType?: string;
  listingType?: string;
  city?: string;
  publicId?: string;
  id?: string;
}): string {
  let titleSlug = sanitizeUrlSegment(params.title || '', 55);

  let citySlug = sanitizeUrlSegment(params.city || '', 30);
  if (!citySlug || citySlug === 'undefined' || citySlug === 'null') {
    citySlug = 'yaounde'; // Default hub in Cameroon if omitted
  }

  // Ensure ID / public ID segment
  let pubId = (params.publicId || '').trim().toLowerCase();
  if (!pubId) {
    if (params.id && /^clm[0-9a-f]{10}$/i.test(params.id)) {
      pubId = params.id.toLowerCase();
    } else if (params.id) {
      pubId = sanitizeUrlSegment(params.id.toLowerCase(), 20);
    } else {
      pubId = generatePublicId();
    }
  }

  // If title was omitted, construct descriptive title from propertyType and transactionType
  if (!titleSlug) {
    const pType = normalizePropertyTypeSlug(params.propertyType);
    const txType = normalizeTransactionTypeSlug(params.listingType);
    titleSlug = `${pType}-${txType}`;
  }

  // If title already ends with the city, do not duplicate city
  let slug: string;
  if (titleSlug.endsWith(`-${citySlug}`)) {
    slug = `${titleSlug}-${pubId}`.toLowerCase();
  } else {
    slug = `${titleSlug}-${citySlug}-${pubId}`.toLowerCase();
  }

  // Reserved slug collision safety
  if (RESERVED_SLUGS.has(slug)) {
    slug = `${slug}-listing`;
  }

  // Maximum slug length rule (100 characters total)
  if (slug.length > 100) {
    const excess = slug.length - 100;
    const trimmedTitle = titleSlug.slice(0, Math.max(10, titleSlug.length - excess)).replace(/-+$/, '');
    if (trimmedTitle.endsWith(`-${citySlug}`)) {
      slug = `${trimmedTitle}-${pubId}`;
    } else {
      slug = `${trimmedTitle}-${citySlug}-${pubId}`;
    }
  }

  return slug;
}

/**
 * Smart Keyword-Intent Slug Generator (Option 3 - Google SEO Best Practice)
 * Produces clean, keyword-optimized slugs based on verified property attributes:
 * - FR: terrain-titre-a-vendre-{city}-{pubId}, appartement-meuble-a-louer-{city}-{pubId}, etc.
 * - EN: titled-land-for-sale-{city}-{pubId}, furnished-apartment-for-rent-{city}-{pubId}, etc.
 */
export function generateSmartPropertySlug(
  params: {
    propertyType?: string;
    property_type?: string;
    type?: string;
    listingType?: string;
    listing_type?: string;
    categoryBadge?: string;
    titreFoncier?: any;
    titre_foncier?: any;
    hasTitle?: boolean;
    has_title?: boolean;
    isFurnished?: boolean;
    is_furnished?: boolean;
    city?: string;
    location?: string;
    publicId?: string;
    public_id?: string;
    id?: string;
    title?: string;
  },
  lang: 'en' | 'fr' = 'en'
): string {
  const pTypeRaw = (params.property_type || params.propertyType || params.type || '').toLowerCase();
  const txTypeRaw = (params.listing_type || params.listingType || params.categoryBadge || '').toLowerCase();
  const titleRaw = (params.title || '').toLowerCase();

  // 1. Detect property type
  let type: 'land' | 'apartment' | 'house' | 'villa' | 'studio' | 'commercial' | 'hotel' = 'land';
  if (pTypeRaw.includes('land') || pTypeRaw.includes('terrain') || pTypeRaw.includes('plot') || pTypeRaw.includes('parcelle')) {
    type = 'land';
  } else if (pTypeRaw.includes('villa')) {
    type = 'villa';
  } else if (pTypeRaw.includes('house') || pTypeRaw.includes('maison') || pTypeRaw.includes('duplex')) {
    type = 'house';
  } else if (pTypeRaw.includes('studio') || pTypeRaw.includes('chambre')) {
    type = 'studio';
  } else if (pTypeRaw.includes('commercial') || pTypeRaw.includes('bureau') || pTypeRaw.includes('office') || pTypeRaw.includes('boutique')) {
    type = 'commercial';
  } else if (pTypeRaw.includes('hotel') || pTypeRaw.includes('stay') || pTypeRaw.includes('residence')) {
    type = 'hotel';
  } else if (pTypeRaw.includes('apartment') || pTypeRaw.includes('appartement')) {
    type = 'apartment';
  }

  // 2. Detect transaction type
  const isRent = txTypeRaw.includes('rent') || txTypeRaw.includes('louer') || txTypeRaw.includes('location') || txTypeRaw.includes('stay');

  // 3. Detect high-intent attributes: Titre Foncier & Furnished
  const hasTitle = Boolean(
    params.has_title ||
    params.hasTitle ||
    params.titre_foncier ||
    params.titreFoncier ||
    titleRaw.includes('titré') ||
    titleRaw.includes('titre') ||
    titleRaw.includes('titled')
  );

  const isFurnished = Boolean(
    params.is_furnished ||
    params.isFurnished ||
    titleRaw.includes('meublé') ||
    titleRaw.includes('meuble') ||
    titleRaw.includes('furnished')
  );

  // 4. Resolve City
  let rawCity = params.city || '';
  if (!rawCity && params.location) {
    rawCity = params.location.split(',')[0].trim();
  }
  let citySlug = sanitizeUrlSegment(rawCity, 30);
  if (!citySlug || citySlug === 'undefined' || citySlug === 'null') {
    citySlug = 'yaounde';
  }

  // 5. Resolve Public ID
  let pubId = (params.public_id || params.publicId || '').trim().toLowerCase();
  if (!pubId && params.id && /^clm[0-9a-f]{10}$/i.test(params.id)) {
    pubId = params.id.toLowerCase();
  }
  if (!pubId && params.id) {
    const parts = params.id.split('-');
    const lastPart = parts[parts.length - 1];
    if (/^clm[0-9a-f]{10}$/i.test(lastPart)) {
      pubId = lastPart.toLowerCase();
    } else {
      pubId = sanitizeUrlSegment(params.id.toLowerCase(), 20);
    }
  }
  if (!pubId) {
    pubId = generatePublicId();
  }

  // 6. Assemble Smart Keyword-Intent Slug
  if (lang === 'fr') {
    if (type === 'land') {
      const prefix = hasTitle ? 'terrain-titre' : 'terrain';
      const action = isRent ? 'a-louer' : 'a-vendre';
      return `${prefix}-${action}-${citySlug}-${pubId}`;
    }
    if (type === 'apartment') {
      const prefix = isFurnished ? 'appartement-meuble' : 'appartement';
      const action = isRent ? 'a-louer' : 'a-vendre';
      return `${prefix}-${action}-${citySlug}-${pubId}`;
    }
    if (type === 'studio') {
      const prefix = isFurnished ? 'studio-meuble' : 'studio';
      const action = isRent ? 'a-louer' : 'a-vendre';
      return `${prefix}-${action}-${citySlug}-${pubId}`;
    }
    if (type === 'villa') {
      const action = isRent ? 'a-louer' : 'a-vendre';
      return `villa-${action}-${citySlug}-${pubId}`;
    }
    if (type === 'house') {
      const action = isRent ? 'a-louer' : 'a-vendre';
      return `maison-${action}-${citySlug}-${pubId}`;
    }
    if (type === 'commercial') {
      const action = isRent ? 'a-louer' : 'a-vendre';
      return `commercial-${action}-${citySlug}-${pubId}`;
    }
    if (type === 'hotel') {
      return `hotel-${citySlug}-${pubId}`;
    }
    return `bien-${isRent ? 'a-louer' : 'a-vendre'}-${citySlug}-${pubId}`;
  }

  // English
  if (type === 'land') {
    const prefix = hasTitle ? 'titled-land' : 'land';
    const action = isRent ? 'for-rent' : 'for-sale';
    return `${prefix}-${action}-${citySlug}-${pubId}`;
  }
  if (type === 'apartment') {
    const prefix = isFurnished ? 'furnished-apartment' : 'apartment';
    const action = isRent ? 'for-rent' : 'for-sale';
    return `${prefix}-${action}-${citySlug}-${pubId}`;
  }
  if (type === 'studio') {
    const prefix = isFurnished ? 'furnished-studio' : 'studio';
    const action = isRent ? 'for-rent' : 'for-sale';
    return `${prefix}-${action}-${citySlug}-${pubId}`;
  }
  if (type === 'villa') {
    const action = isRent ? 'for-rent' : 'for-sale';
    return `villa-${action}-${citySlug}-${pubId}`;
  }
  if (type === 'house') {
    const action = isRent ? 'for-rent' : 'for-sale';
    return `house-${action}-${citySlug}-${pubId}`;
  }
  if (type === 'commercial') {
    const action = isRent ? 'for-rent' : 'for-sale';
    return `commercial-${action}-${citySlug}-${pubId}`;
  }
  if (type === 'hotel') {
    return `hotel-${citySlug}-${pubId}`;
  }
  return `property-${isRent ? 'for-rent' : 'for-sale'}-${citySlug}-${pubId}`;
}

const PROPERTY_SLUG_PREFIX_PAIRS: Array<[string, string]> = [
  ['titled-land-for-sale-', 'terrain-titre-a-vendre-'],
  ['titled-land-for-rent-', 'terrain-titre-a-louer-'],
  ['land-for-sale-', 'terrain-a-vendre-'],
  ['land-for-rent-', 'terrain-a-louer-'],
  ['furnished-apartment-for-rent-', 'appartement-meuble-a-louer-'],
  ['furnished-apartment-for-sale-', 'appartement-meuble-a-vendre-'],
  ['apartment-for-rent-', 'appartement-a-louer-'],
  ['apartment-for-sale-', 'appartement-a-vendre-'],
  ['furnished-studio-for-rent-', 'studio-meuble-a-louer-'],
  ['studio-for-rent-', 'studio-a-louer-'],
  ['studio-for-sale-', 'studio-a-vendre-'],
  ['villa-for-sale-', 'villa-a-vendre-'],
  ['villa-for-rent-', 'villa-a-louer-'],
  ['house-for-sale-', 'maison-a-vendre-'],
  ['house-for-rent-', 'maison-a-louer-'],
  ['commercial-for-sale-', 'commercial-a-vendre-'],
  ['commercial-for-rent-', 'commercial-a-louer-'],
  ['offices-for-rent-', 'bureaux-a-louer-'],
  ['shops-for-rent-', 'boutiques-a-louer-'],
  ['property-for-sale-', 'bien-a-vendre-'],
  ['property-for-rent-', 'bien-a-louer-'],
];

/**
 * Translates property slugs between English and French deterministically.
 */
export function getLocalizedPropertySlug(slug: string, targetLang: 'en' | 'fr'): string {
  if (!slug) return slug;
  const lower = slug.toLowerCase().trim();

  if (targetLang === 'fr') {
    for (const [enPref, frPref] of PROPERTY_SLUG_PREFIX_PAIRS) {
      if (lower.startsWith(enPref)) {
        return `${frPref}${lower.slice(enPref.length)}`;
      }
    }
    // If slug already has French prefix, return as-is
    for (const [, frPref] of PROPERTY_SLUG_PREFIX_PAIRS) {
      if (lower.startsWith(frPref)) return lower;
    }
    return slug;
  }

  // targetLang === 'en'
  for (const [enPref, frPref] of PROPERTY_SLUG_PREFIX_PAIRS) {
    if (lower.startsWith(frPref)) {
      return `${enPref}${lower.slice(frPref.length)}`;
    }
  }
  // If slug already has English prefix, return as-is
  for (const [enPref] of PROPERTY_SLUG_PREFIX_PAIRS) {
    if (lower.startsWith(enPref)) return lower;
  }

  return slug;
}

/**
 * Helper to determine current active language safely in SSR/browser contexts.
 */
export function getCurrentLanguage(): 'en' | 'fr' {
  if (typeof window !== 'undefined') {
    const p = window.location.pathname.toLowerCase();
    if (p === '/fr' || p.startsWith('/fr/')) return 'fr';
    if (p === '/en' || p.startsWith('/en/')) return 'en';
    try {
      const match = document.cookie.match(/(?:^|;\s*)clm_language=([^;]+)/);
      if (match && (match[1] === 'en' || match[1] === 'fr')) return match[1] as 'en' | 'fr';
      const saved = localStorage.getItem('clm_language') || localStorage.getItem('cameroon_estate_lang');
      if (saved === 'fr' || saved === 'en') return saved;
    } catch {
      // Storage access blocked or SSR
    }
  }
  return 'en';
}

/**
 * Computes the exact localized equivalent URL for any given pathname in targetLang ('en' | 'fr').
 * Preserves query string if provided.
 */
export function getLocalizedEquivalentUrl(
  pathname: string,
  targetLang: 'en' | 'fr',
  currentSearch?: string
): string {
  const rawPath = (pathname || '/').split('?')[0].split('#')[0];
  const cleanPath = rawPath.replace(/\/+$/, '') || '/';
  const lower = cleanPath.toLowerCase();

  let targetPath = '';

  // Internal Vault & Admin routes should NEVER be prefixed with /en or /fr
  if (
    lower.startsWith('/clm-master-vault-9820') ||
    lower.startsWith('/admin') ||
    lower.startsWith('/__')
  ) {
    return cleanPath;
  }

  // 1. Root / Home
  if (lower === '/' || lower === '/en' || lower === '/fr') {
    targetPath = targetLang === 'fr' ? '/fr' : '/en';
  }

  // 2. Listing & Property detail pages (/en/property/:slug, /fr/propriete/:slug, /en/listing/:slug, etc.)
  if (!targetPath) {
    const propMatch = lower.match(/^\/(?:en\/|fr\/)?(?:listing|annonce|property|propriete|properties)\/([^/?#]+)$/i);
    if (propMatch) {
      const slug = propMatch[1];
      const localizedSlug = getLocalizedPropertySlug(slug, targetLang);
      targetPath = targetLang === 'fr' ? `/fr/propriete/${localizedSlug}` : `/en/property/${localizedSlug}`;
    }
  }

  // 2.5. News & Editorial article pages (/en/news/:slug <-> /fr/actualites/:slug)
  if (!targetPath) {
    const newsMatch = lower.match(/^\/(?:en\/|fr\/)?(?:news|actualites)(?:\/([^/?#]+))?$/i);
    if (newsMatch) {
      const slug = newsMatch[1];
      if (slug) {
        targetPath = targetLang === 'fr' ? `/fr/actualites/${slug}` : `/en/news/${slug}`;
      } else {
        targetPath = targetLang === 'fr' ? '/fr/actualites' : '/en/news';
      }
    }
  }

  // 3. Hotel detail pages (/en/hotel/:slug, /fr/hotel/:slug, /hotel/:slug, /short-stay/:slug)
  if (!targetPath) {
    const hotelMatch = lower.match(/^\/(?:en\/|fr\/)?(?:hotel|short-stay)\/([^/?#]+)$/i);
    if (hotelMatch) {
      const slug = hotelMatch[1];
      targetPath = targetLang === 'fr' ? `/fr/hotel/${slug}` : `/en/hotel/${slug}`;
    }
  }

  // 3.5 Region pages (/en/regions/:slug, /fr/regions/:slug, /regions/:slug)
  if (!targetPath) {
    const regMatch = lower.match(/^\/(?:en\/|fr\/)?regions(?:\/([^/?#]+))?$/i);
    if (regMatch) {
      const regSlug = regMatch[1];
      if (regSlug) {
        const reg = getRegionBySlug(regSlug);
        if (reg) {
          targetPath = targetLang === 'fr' ? `/fr/regions/${reg.slugFr}` : `/en/regions/${reg.slug}`;
        } else {
          targetPath = targetLang === 'fr' ? `/fr/regions/${regSlug}` : `/en/regions/${regSlug}`;
        }
      } else {
        targetPath = targetLang === 'fr' ? '/fr/regions' : '/en/regions';
      }
    }
  }

  // 4. Cities pages (/en/cities/:slug, /fr/villes/:slug, /cities/:slug, /villes/:slug, /cameroon-cities)
  if (!targetPath) {
    const cityMatch = lower.match(/^\/(?:en\/|fr\/)?(?:cities|villes)\/([^/?#]+)$/i);
    if (cityMatch) {
      const citySlug = cityMatch[1];
      targetPath = targetLang === 'fr' ? `/fr/villes/${citySlug}` : `/en/cities/${citySlug}`;
    } else if (/^\/(?:en\/|fr\/)?(?:cities|villes|cameroon-cities)$/i.test(lower)) {
      targetPath = targetLang === 'fr' ? '/fr/villes' : '/en/cities';
    }
  }

  // 5. Professionals pages (/en/professionals/:category, /fr/professionnels/:category, etc.)
  if (!targetPath) {
    const proMatch = lower.match(/^\/(?:en\/|fr\/)?(?:professionals|professionnels)(?:\/([^/?#]+))?$/i);
    if (proMatch) {
      const cat = proMatch[1];
      if (cat) {
        const canonicalKey = REVERSE_PROFESSIONAL_SLUGS[cat] || cat;
        const proSlug = targetLang === 'fr'
          ? (PROFESSIONAL_SLUGS_FR[canonicalKey] || cat)
          : (PROFESSIONAL_SLUGS[canonicalKey] || cat);
        targetPath = targetLang === 'fr' ? `/fr/professionnels/${proSlug}` : `/en/professionals/${proSlug}`;
      } else {
        targetPath = targetLang === 'fr' ? '/fr/professionnels' : '/en/professionals';
      }
    } else if (/^\/(?:en\/|fr\/)?agents$/i.test(lower)) {
      targetPath = targetLang === 'fr' ? '/fr/professionnels/agents' : '/en/professionals/agents';
    }
  }

  // 6. Resources pages (/en/resources/:topic, /fr/ressources/:topic, etc.)
  if (!targetPath) {
    const resMatch = lower.match(/^\/(?:en\/|fr\/)?(?:resources|ressources)(?:\/([^/?#]+))?$/i);
    if (resMatch) {
      const topic = resMatch[1];
      if (topic) {
        const canonicalKey = REVERSE_RESOURCE_TOPIC_SLUGS[topic] || topic;
        const topicSlug = targetLang === 'fr'
          ? (RESOURCE_TOPIC_SLUGS_FR[canonicalKey] || topic)
          : (RESOURCE_TOPIC_SLUGS[canonicalKey] || topic);
        targetPath = targetLang === 'fr' ? `/fr/ressources/${topicSlug}` : `/en/resources/${topicSlug}`;
      } else {
        targetPath = targetLang === 'fr' ? '/fr/ressources' : '/en/resources';
      }
    } else if (/^\/(?:en\/|fr\/)?guides$/i.test(lower)) {
      targetPath = targetLang === 'fr' ? '/fr/ressources' : '/en/resources';
    }
  }

  // 7. Core Category Pages & Hierarchical Location URLs (e.g. /en/properties-for-sale/south-west/buea <-> /fr/biens-a-vendre/sud-ouest/buea)
  if (!targetPath) {
    const catMatch = lower.match(/^\/(?:en\/|fr\/)?([a-z0-9-]+)(?:\/(.+))?$/i);
    if (catMatch) {
      const baseSegment = catMatch[1];

      const matchedConfig = CATEGORY_ROUTES.find(
        (c) => c.slug === baseSegment || c.slugFr === baseSegment
      );

      if (matchedConfig) {
        const primaryConfig =
          CATEGORY_ROUTES.find((c) => c.slug === matchedConfig.slug && !c.id.endsWith('-alias')) ||
          matchedConfig;
        let localizedRest = '';
        if (catMatch[2]) {
          const parts = catMatch[2].split('/').filter(Boolean);
          if (parts.length > 0) {
            const firstPart = parts[0];
            const reg = getRegionBySlug(firstPart);
            if (reg) {
              const regSlug = targetLang === 'fr' ? reg.slugFr : reg.slug;
              const remaining = parts.slice(1);
              localizedRest = `/${[regSlug, ...remaining].join('/')}`;
            } else {
              localizedRest = `/${parts.join('/')}`;
            }
          }
        }
        targetPath = targetLang === 'fr'
          ? `/fr/${primaryConfig.slugFr}${localizedRest}`
          : `/en/${primaryConfig.slug}${localizedRest}`;
      }
    }
  }

  // 8. Static Pages in LOCALIZED_ROUTES
  if (!targetPath) {
    for (const key of Object.keys(LOCALIZED_ROUTES)) {
      const route = LOCALIZED_ROUTES[key];
      const enLower = route.en.toLowerCase();
      const frLower = route.fr.toLowerCase();
      const enUnprefixed = enLower.replace(/^\/en/, '');
      const frUnprefixed = frLower.replace(/^\/fr/, '');

      if (lower === enLower || lower === frLower || lower === enUnprefixed || lower === frUnprefixed) {
        targetPath = route[targetLang];
        break;
      }
    }
  }

  // 9. Extra aliases: /carte -> /fr/carte, /map -> /en/map, /verify-land -> /en/verify-property or /fr/verifier-propriete
  if (!targetPath) {
    if (lower === '/carte' || lower === '/map' || lower === '/fr/map' || lower === '/en/carte') {
      targetPath = targetLang === 'fr' ? '/fr/carte' : '/en/map';
    } else if (lower === '/verifier-propriete' || lower === '/verify-property' || lower === '/verify-land' || lower === '/fr/verify-property') {
      targetPath = targetLang === 'fr' ? '/fr/verifier-propriete' : '/en/verify-property';
    } else if (lower === '/verifier-titre-foncier' || lower === '/verify-titre-foncier' || lower === '/fr/verify-titre-foncier') {
      targetPath = targetLang === 'fr' ? '/fr/verifier-titre-foncier' : '/en/verify-titre-foncier';
    } else if (lower === '/deposer-annonce' || lower === '/publier-annonce' || lower === '/post' || lower === '/post-property' || lower === '/fr/post-property' || lower === '/fr/post') {
      targetPath = targetLang === 'fr' ? '/fr/deposer-annonce' : '/en/post-property';
    } else if (lower === '/a-propos' || lower === '/about' || lower === '/fr/about') {
      targetPath = targetLang === 'fr' ? '/fr/a-propos' : '/en/about';
    } else if (lower === '/tableau-de-bord' || lower === '/dashboard' || lower === '/account' || lower === '/fr/dashboard' || lower === '/fr/account') {
      targetPath = targetLang === 'fr' ? '/fr/tableau-de-bord' : '/en/dashboard';
    } else if (lower === '/mes-annonces' || lower === '/manage-listings' || lower === '/my-listings' || lower === '/fr/manage-listings' || lower === '/fr/my-listings') {
      targetPath = targetLang === 'fr' ? '/fr/mes-annonces' : '/en/manage-listings';
    } else if (lower === '/mon-profil' || lower === '/profile' || lower === '/fr/profile') {
      targetPath = targetLang === 'fr' ? '/fr/mon-profil' : '/en/profile';
    } else if (lower === '/plan-du-site' || lower === '/sitemap' || lower === '/html-sitemap' || lower === '/fr/sitemap' || lower === '/fr/html-sitemap') {
      targetPath = targetLang === 'fr' ? '/fr/plan-du-site' : '/en/sitemap';
    } else if (lower === '/mot-de-passe-oublie' || lower === '/forgot-password' || lower === '/fr/forgot-password') {
      targetPath = targetLang === 'fr' ? '/fr/mot-de-passe-oublie' : '/en/forgot-password';
    } else if (lower === '/reinitialiser-mot-de-passe' || lower === '/reset-password' || lower === '/fr/reset-password') {
      targetPath = targetLang === 'fr' ? '/fr/reinitialiser-mot-de-passe' : '/en/reset-password';
    } else if (lower === '/connexion' || lower === '/signin' || lower === '/sign-in' || lower === '/fr/signin' || lower === '/fr/sign-in') {
      targetPath = targetLang === 'fr' ? '/fr/connexion' : '/en/signin';
    } else if (lower === '/inscription' || lower === '/signup' || lower === '/sign-up' || lower === '/fr/signup' || lower === '/fr/sign-up') {
      targetPath = targetLang === 'fr' ? '/fr/inscription' : '/en/signup';
    } else if (lower === '/politique-confidentialite' || lower === '/fr/politique-confidentialite' || lower === '/confidentialite' || lower === '/fr/confidentialite' || lower === '/privacy' || lower === '/en/privacy' || lower === '/fr/privacy') {
      targetPath = targetLang === 'fr' ? '/fr/confidentialite' : '/en/privacy';
    } else if (lower === '/conditions-utilisation' || lower === '/conditions' || lower === '/fr/conditions' || lower === '/terms' || lower === '/fr/terms') {
      targetPath = targetLang === 'fr' ? '/fr/conditions-utilisation' : '/en/terms';
    }
  }

  // 10. Fallback: normalize prefix to targetLang
  if (!targetPath) {
    let stripped = cleanPath;
    if (stripped.startsWith('/fr/')) stripped = stripped.slice(3);
    else if (stripped.startsWith('/en/')) stripped = stripped.slice(3);
    else if (stripped === '/fr' || stripped === '/en') stripped = '';
    targetPath = targetLang === 'fr' ? `/fr${stripped}` : `/en${stripped}`;
  }

  // Append query string if present
  if (currentSearch && currentSearch.trim() && currentSearch !== '?') {
    const searchPart = currentSearch.startsWith('?') ? currentSearch : `?${currentSearch}`;
    return `${targetPath}${searchPart}`;
  }

  return targetPath;
}

/**
 * Generates an SEO listing URL with slug from title + city + id.
 * Defaults to current active language ('en' -> /en/listing/..., 'fr' -> /fr/listing/...).
 */
export function buildPropertyUrl(
  property: {
    id: string;
    title?: string;
    title_en?: string;
    title_fr?: string;
    location?: string;
    type?: string;
    property_type?: string;
    propertyType?: string;
    listingType?: string;
    listing_type?: string;
    categoryBadge?: string;
    slug?: string;
    slug_en?: string;
    slug_fr?: string;
    slugEn?: string;
    slugFr?: string;
    publicId?: string;
    public_id?: string;
    titreFoncier?: any;
    titre_foncier?: any;
    hasTitle?: boolean;
    has_title?: boolean;
    isFurnished?: boolean;
    is_furnished?: boolean;
    city?: string;
  },
  language?: 'en' | 'fr'
): string {
  const lang = language || getCurrentLanguage();
  const prefix = lang === 'fr' ? '/fr/propriete' : '/en/property';

  // 1. Check explicit bilingual slug fields
  const explicitSlug = lang === 'fr'
    ? (property.slug_fr || property.slugFr)
    : (property.slug_en || property.slugEn);
  if (explicitSlug && /^[a-z0-9-]+-clm[0-9a-f]{10}$/i.test(explicitSlug)) {
    return `${prefix}/${explicitSlug.toLowerCase()}`;
  }

  // 2. If property has a slug, translate the slug prefix to the active language
  if (property.slug && /^[a-z0-9-]+-clm[0-9a-f]{10}$/i.test(property.slug)) {
    const localized = getLocalizedPropertySlug(property.slug, lang);
    return `${prefix}/${localized.toLowerCase()}`;
  }

  // 3. Construct Smart Keyword-Intent Slug (Option 3)
  const smartSlug = generateSmartPropertySlug(property, lang);
  return `${prefix}/${smartSlug.toLowerCase()}`;
}

export const buildListingUrl = buildPropertyUrl;

/**
 * Generates an SEO hotel listing URL with slug and unique ID.
 */
export function buildHotelUrl(
  property: { id: string; title: string; location?: string; slug?: string; publicId?: string },
  language?: 'en' | 'fr'
): string {
  const lang = language || getCurrentLanguage();
  const prefix = lang === 'fr' ? '/fr/hotel' : '/en/hotel';

  if (property.slug) {
    return `${prefix}/${property.slug.toLowerCase()}`;
  }
  const city = property.location ? property.location.split(',')[0].trim() : 'yaounde';
  const slug = generatePropertySlug({
    propertyType: 'hotel',
    listingType: 'rent',
    city,
    publicId: property.publicId,
    id: property.id,
  });
  return `${prefix}/${slug}`;
}

/**
 * Extracts unique property public ID from a slug.
 * Accurately parses:
 * - Modern format: "apartment-for-rent-douala-clm73a0f895c2" -> "clm73a0f895c2"
 * - Raw public ID: "clm73a0f895c2" -> "clm73a0f895c2"
 * - Legacy slug format: "luxury-villa-bastos-pr-12345" -> "pr-12345"
 */
export function parsePropertyIdFromSlug(slugParam: string, knownIds?: string[]): string {
  if (!slugParam) return '';
  const decoded = decodeURIComponent(slugParam).trim().toLowerCase();

  // 1. Check for clm public ID pattern: e.g. "apartment-for-rent-douala-clm73a0f895c2"
  const clmMatch = decoded.match(/(clm[0-9a-f]{10}|clm[a-z0-9]{4,12})$/i);
  if (clmMatch) {
    return clmMatch[1].toLowerCase();
  }

  // If known IDs provided and exact match exists, return it
  if (knownIds && knownIds.includes(decoded)) {
    return decoded;
  }

  // 2. Check if known ID is the suffix of the slug: [any-text]-[knownId]
  if (knownIds) {
    for (const id of knownIds) {
      if (decoded.endsWith(`-${id.toLowerCase()}`)) {
        return id;
      }
    }
  }

  // 3. Fallback: extracts the final hyphen-delimited segment
  const parts = decoded.split('-');
  if (parts.length <= 1) {
    return decoded;
  }

  // If the last part is prefixed with 'prop' or 'pr', take the last 2 parts
  const lastPart = parts[parts.length - 1];
  const secondLast = parts[parts.length - 2];
  if (secondLast === 'prop' || secondLast === 'pr') {
    return `${secondLast}-${lastPart}`;
  }

  return lastPart;
}

/**
 * Generates localized category URL optionally scoped by city
 */
export function buildCategoryUrl(
  transaction: 'sale' | 'rent' | 'stay',
  category?: string,
  citySlug?: string,
  language?: 'en' | 'fr'
): string {
  const lang = language || getCurrentLanguage();
  let matchedConfig: CategoryRouteConfig | undefined;

  if (category && category !== 'all') {
    matchedConfig = CATEGORY_ROUTES.find(
      (c) => c.transaction === transaction && (c.filterType === category || c.slug === category || c.slugFr === category)
    );
  }

  if (!matchedConfig) {
    matchedConfig = CATEGORY_ROUTES.find(
      (c) => c.transaction === transaction && c.filterType === 'all'
    );
  }

  const baseSlug = matchedConfig
    ? (lang === 'fr' ? matchedConfig.slugFr : matchedConfig.slug)
    : (lang === 'fr' ? 'biens-a-vendre' : 'properties-for-sale');

  const langPrefix = lang === 'fr' ? '/fr' : '/en';
  if (citySlug) {
    return `${langPrefix}/${baseSlug}/${slugify(citySlug)}`;
  }
  return `${langPrefix}/${baseSlug}`;
}

/**
 * Generates localized city overview URL
 */
export function buildCityUrl(citySlug: string, language?: 'en' | 'fr'): string {
  const lang = language || getCurrentLanguage();
  return lang === 'fr' ? `/fr/villes/${slugify(citySlug)}` : `/en/cities/${slugify(citySlug)}`;
}

/**
 * Generates localized professional directory URL
 */
export function buildProfessionalUrl(category?: string, language?: 'en' | 'fr'): string {
  const lang = language || getCurrentLanguage();
  const isFr = lang === 'fr';
  const prefix = isFr ? '/fr/professionnels' : '/en/professionals';
  if (!category || category === 'all') return prefix;
  const slugMap = isFr ? PROFESSIONAL_SLUGS_FR : PROFESSIONAL_SLUGS;
  const canonicalKey = REVERSE_PROFESSIONAL_SLUGS[category] || category;
  const proSlug = slugMap[canonicalKey] || slugify(category);
  return `${prefix}/${proSlug}`;
}

/**
 * Generates localized resources legal guide URL
 */
export function buildResourceUrl(tabId?: string, language?: 'en' | 'fr'): string {
  const lang = language || getCurrentLanguage();
  const isFr = lang === 'fr';
  const prefix = isFr ? '/fr/ressources' : '/en/resources';
  if (!tabId || tabId === 'land-guide' || tabId === 'land-buying-guide' || tabId === 'guide-achat-terrain') {
    return `${prefix}/${isFr ? 'guide-achat-terrain' : 'land-buying-guide'}`;
  }
  const slugMap = isFr ? RESOURCE_TOPIC_SLUGS_FR : RESOURCE_TOPIC_SLUGS;
  const canonicalKey = REVERSE_RESOURCE_TOPIC_SLUGS[tabId] || tabId;
  const topicSlug = slugMap[canonicalKey] || slugify(tabId);
  return `${prefix}/${topicSlug}`;
}

export interface TrustedCityConfig {
  slug: string;
  name: string;
  frenchPreposition: string;
  region: string;
}

export const TRUSTED_CAMEROON_CITIES: Record<string, TrustedCityConfig> = {
  yaounde: { slug: 'yaounde', name: 'Yaoundé', frenchPreposition: 'à ', region: 'Centre' },
  douala: { slug: 'douala', name: 'Douala', frenchPreposition: 'à ', region: 'Littoral' },
  kribi: { slug: 'kribi', name: 'Kribi', frenchPreposition: 'à ', region: 'South' },
  buea: { slug: 'buea', name: 'Buea', frenchPreposition: 'à ', region: 'South-West' },
  limbe: { slug: 'limbe', name: 'Limbe', frenchPreposition: 'à ', region: 'South-West' },
  bafoussam: { slug: 'bafoussam', name: 'Bafoussam', frenchPreposition: 'à ', region: 'West' },
  bamenda: { slug: 'bamenda', name: 'Bamenda', frenchPreposition: 'à ', region: 'North-West' },
  garoua: { slug: 'garoua', name: 'Garoua', frenchPreposition: 'à ', region: 'North' },
  maroua: { slug: 'maroua', name: 'Maroua', frenchPreposition: 'à ', region: 'Far North' },
  ngaoundere: { slug: 'ngaoundere', name: 'Ngaoundéré', frenchPreposition: 'à ', region: 'Adamawa' },
  bertoua: { slug: 'bertoua', name: 'Bertoua', frenchPreposition: 'à ', region: 'East' },
  ebolowa: { slug: 'ebolowa', name: 'Ebolowa', frenchPreposition: 'à ', region: 'South' },
  kumba: { slug: 'kumba', name: 'Kumba', frenchPreposition: 'à ', region: 'South-West' },
  edea: { slug: 'edea', name: 'Edéa', frenchPreposition: 'à ', region: 'Littoral' },
  dschang: { slug: 'dschang', name: 'Dschang', frenchPreposition: 'à ', region: 'West' },
  foumban: { slug: 'foumban', name: 'Foumban', frenchPreposition: 'à ', region: 'West' },
  bafia: { slug: 'bafia', name: 'Bafia', frenchPreposition: 'à ', region: 'Centre' },
  sangmelima: { slug: 'sangmelima', name: 'Sangmélima', frenchPreposition: 'à ', region: 'South' },
  mbalmayo: { slug: 'mbalmayo', name: 'Mbalmayo', frenchPreposition: 'à ', region: 'Centre' },
  tiko: { slug: 'tiko', name: 'Tiko', frenchPreposition: 'à ', region: 'South-West' },
};

/**
 * Validates a city slug against trusted Cameroon cities and authentic directory towns.
 * Returns the TrustedCityConfig or null if the slug is unsupported.
 */
export function getTrustedCity(citySlug?: string): TrustedCityConfig | null {
  if (!citySlug) return null;
  const cleaned = sanitizeUrlSegment(citySlug, 50).toLowerCase();
  if (!cleaned) return null;

  if (TRUSTED_CAMEROON_CITIES[cleaned]) {
    return TRUSTED_CAMEROON_CITIES[cleaned];
  }

  // Check direct matches in TRUSTED_CAMEROON_CITIES with composite region
  for (const [key, val] of Object.entries(TRUSTED_CAMEROON_CITIES)) {
    const regSlug = slugify(val.region);
    if (cleaned === `${key}-${regSlug}` || cleaned === `${key}-${regSlug}-region` || cleaned.startsWith(`${key}-`)) {
      return val;
    }
  }

  const normalizedCleaned = cleaned.replace(/[-_]/g, '');
  const town = ALL_CAMEROON_TOWNS.find(
    (t) =>
      t.slug.toLowerCase().replace(/[-_]/g, '') === normalizedCleaned ||
      slugify(t.name) === cleaned
  );
  if (town) {
    return {
      slug: town.slug,
      name: town.name,
      frenchPreposition: 'à ',
      region: town.region,
    };
  }

  // Support composite city-region slugs (e.g., dibombari-littoral, dizangue-littoral)
  const townWithRegion = ALL_CAMEROON_TOWNS.find((t) => {
    const regSlug = slugify(t.region);
    return (
      cleaned === `${t.slug}-${regSlug}` ||
      cleaned === `${slugify(t.name)}-${regSlug}` ||
      cleaned.startsWith(`${t.slug}-`)
    );
  });
  if (townWithRegion) {
    return {
      slug: townWithRegion.slug,
      name: townWithRegion.name,
      frenchPreposition: 'à ',
      region: townWithRegion.region,
    };
  }

  return null;
}

/**
 * Returns a validated authentic Cameroon city name from URL slug, or null if unsupported.
 */
export function getValidatedCityName(citySlug?: string): string | null {
  const city = getTrustedCity(citySlug);
  return city ? city.name : null;
}

export interface ListingMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
  canonicalPath: string;
  enCanonicalUrl?: string;
  frCanonicalUrl?: string;
  ogType: 'article' | 'website';
  noIndex: boolean;
}

/**
 * Unified publication rule for public/indexable listings across Cameroon Land Market.
 * A listing is ONLY public and indexable if its verified status is 'active', 'published', or 'approved'.
 * Strictly excludes: drafts, pending, pending_review, rejected, archived, deleted, sold, rented, expired, etc.
 */
export function isListingPublicAndIndexable(property?: {
  status?: string;
  is_published?: boolean;
} | null): boolean {
  if (!property) return false;
  const status = (property.status || '').toLowerCase().trim();
  if (
    status === 'draft' ||
    status === 'pending' ||
    status === 'pending_review' ||
    status === 'rejected' ||
    status === 'archived' ||
    status === 'deleted' ||
    status === 'sold' ||
    status === 'rented' ||
    status === 'expired'
  ) {
    return false;
  }
  return (
    status === 'active' ||
    status === 'published' ||
    status === 'approved' ||
    (!status && property.is_published === true)
  );
}

/**
 * Generates safe, verified dynamic listing metadata matching exact required templates:
 * Supports localizedTitle and localizedDescription.
 * Provides self-referencing canonical URL and reciprocal hreflang URLs.
 */
export function buildSafeListingMetadata(
  property: {
    id?: string;
    publicId?: string;
    slug?: string;
    title?: string;
    title_en?: string;
    title_fr?: string;
    description?: string;
    description_en?: string;
    description_fr?: string;
    original_lang?: string;
    type?: string;
    propertyType?: string;
    price?: string | number;
    priceRaw?: number;
    location?: string;
    city?: string;
    region?: string;
    status?: string;
    isVerified?: boolean;
    localizedTitle?: { en?: string; fr?: string };
    localizedDescription?: { en?: string; fr?: string };
  },
  language: 'en' | 'fr' = 'en'
): ListingMetadata {
  const isFr = language === 'fr';
  const stripHtml = (s: string) =>
    (s || '')
      .replace(/<[^>]*>/g, '')
      .replace(/[\r\n\t]+/g, ' ')
      .trim();

  // Title: prioritize explicit meta title or localized title matching requested language
  const explicitMetaTitle = isFr
    ? ((property as any).meta_title_fr || (property as any).seoTitleFr)
    : ((property as any).meta_title || (property as any).seoTitle);

  const chosenTitle = explicitMetaTitle ||
    (isFr ? (property.title_fr || property.localizedTitle?.fr) : (property.title_en || property.localizedTitle?.en)) ||
    property.title;

  let rawTitle = stripHtml(chosenTitle || (isFr ? 'Annonce Immobilière' : 'Property Listing'))
    .replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

  // If title is in ALL CAPS, convert to Title Case for Google search quality guidelines
  if (rawTitle.length > 5 && rawTitle.toUpperCase() === rawTitle && /[A-Z]/.test(rawTitle)) {
    rawTitle = rawTitle.toLowerCase().replace(/(?:^|\s|\/|-|\()\w/g, (c) => c.toUpperCase());
  }

  // If title is long, extract clean leading clause before delimiter (e.g. "!", "|", "?")
  let safeTitle = rawTitle;
  if (safeTitle.length > 60) {
    const clauseMatch = safeTitle.match(/^([^!|?]+)/);
    if (clauseMatch && clauseMatch[1].trim().length >= 20 && clauseMatch[1].trim().length <= 60) {
      safeTitle = clauseMatch[1].trim();
    } else {
      safeTitle = safeTitle.slice(0, 57).replace(/\s+[^\s]*$/, '').trim();
      if (!safeTitle) safeTitle = rawTitle.slice(0, 57).trim();
    }
  }

  let city = property.city ? stripHtml(property.city) : '';
  let region = property.region ? stripHtml(property.region) : '';
  if (!city && property.location) {
    const parts = property.location.split(',').map((p) => stripHtml(p.trim()));
    city = parts[0] || '';
    if (parts[1] && !region) region = parts[1];
  }
  if (!city) city = isFr ? 'au Cameroun' : 'Cameroon';

  let formattedPrice = '';
  if (typeof property.priceRaw === 'number' && property.priceRaw > 0) {
    formattedPrice = property.priceRaw.toLocaleString(isFr ? 'fr-FR' : 'en-US');
  } else if (typeof property.price === 'string' && property.price.trim()) {
    const num = Number(property.price.replace(/[^\d]/g, ''));
    formattedPrice = num > 0 ? num.toLocaleString(isFr ? 'fr-FR' : 'en-US') : '';
  }

  const rawType = stripHtml(property.type || property.propertyType || 'Property').toLowerCase();
  const typeCapitalized = isFr
    ? rawType === 'land'
      ? 'Terrain'
      : rawType === 'house'
      ? 'Maison'
      : rawType === 'apartment'
      ? 'Appartement'
      : rawType === 'studio'
      ? 'Studio meublé'
      : rawType === 'commercial'
      ? 'Boutiques & Bureaux'
      : rawType === 'hotel'
      ? 'Hôtel'
      : 'Propriété'
    : rawType === 'commercial'
    ? 'Shops & Offices'
    : rawType.charAt(0).toUpperCase() + rawType.slice(1);

  const cityJoin = isFr ? 'à' : 'in';
  const rawSeoTitle = safeTitle.toLowerCase().includes(city.toLowerCase())
    ? safeTitle
    : `${safeTitle} ${cityJoin} ${city}`;
  const title = rawSeoTitle.length > 60 ? `${rawSeoTitle.slice(0, 57).trim()}...` : rawSeoTitle;

  const locationDesc =
    region && region.toLowerCase() !== city.toLowerCase() ? `${city}, ${region}` : city;

  // Description: prioritize explicit meta description or localized description
  const explicitMetaDesc = isFr
    ? ((property as any).meta_description_fr || (property as any).seoDescriptionFr)
    : ((property as any).meta_description || (property as any).seoDescription);

  const chosenDesc = explicitMetaDesc ||
    (isFr ? (property.description_fr || property.localizedDescription?.fr) : (property.description_en || property.localizedDescription?.en)) ||
    property.description;
  let description = '';
  if (chosenDesc && chosenDesc.trim()) {
    const cleanDesc = stripHtml(chosenDesc)
      .replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
    description = cleanDesc.length > 160 ? `${cleanDesc.slice(0, 157).replace(/\s+[^\s]*$/, '')}...` : cleanDesc;
  } else {
    description = isFr
      ? formattedPrice
        ? `${typeCapitalized} disponible à ${locationDesc} au prix de ${formattedPrice} XAF. Consultez les photos, caractéristiques, localisation et coordonnées du vendeur.`
        : `${typeCapitalized} disponible à ${locationDesc}. Consultez les photos, caractéristiques, localisation et coordonnées du vendeur.`
      : formattedPrice
      ? `${typeCapitalized} available in ${locationDesc} for ${formattedPrice} XAF. View photos, location, property details and seller contact options.`
      : `${typeCapitalized} available in ${locationDesc}. View photos, location, property details and seller contact options.`;
  }

  const canonicalPath = buildPropertyUrl(property as any, language);
  const canonicalUrl = `${CANONICAL_DOMAIN}${canonicalPath}`;
  const enCanonicalUrl = `${CANONICAL_DOMAIN}${buildPropertyUrl(property as any, 'en')}`;
  const frCanonicalUrl = `${CANONICAL_DOMAIN}${buildPropertyUrl(property as any, 'fr')}`;

  const isPublic = isListingPublicAndIndexable(property);

  return {
    title,
    description,
    canonicalPath,
    canonicalUrl,
    enCanonicalUrl,
    frCanonicalUrl,
    ogType: 'article',
    noIndex: !isPublic,
  };
}

/**
 * Returns a self-referencing canonical URL.
 * Canonical tags on English pages point to the English URL (https://cameroonlandmarket.com/en/...)
 * Canonical tags on French pages point to the French URL (https://cameroonlandmarket.com/fr/...)
 * Every page strictly canonicalizes to its clean URL without query parameters.
 */
export function getCanonicalUrl(pathname: string, language?: 'en' | 'fr', _preserveQuery = false): string {
  const [pathPart] = (pathname || '/').split('#')[0].split('?');
  const cleanPath = pathPart.replace(/\/+$/, '') || '/';
  const lowerPath = cleanPath.toLowerCase();

  let base = '';
  // If already localized with /en or /fr
  if (lowerPath === '/fr' || lowerPath === '/fr/') {
    base = `${CANONICAL_DOMAIN}/fr`;
  } else if (lowerPath === '/' || lowerPath === '/en' || lowerPath === '/en/') {
    base = `${CANONICAL_DOMAIN}/en`;
  } else if (lowerPath.startsWith('/fr/')) {
    base = `${CANONICAL_DOMAIN}${cleanPath}`;
  } else if (lowerPath.startsWith('/en/')) {
    base = `${CANONICAL_DOMAIN}${cleanPath}`;
  } else {
    // Unprefixed legacy route: convert to target language canonical
    const lang = language || getCurrentLanguage();
    const localized = getLocalizedEquivalentUrl(cleanPath || '/', lang);
    base = `${CANONICAL_DOMAIN}${localized}`;
  }

  // Canonical tag must strictly be self-referencing to its clean URL without query parameters
  return base;
}

/**
 * Generates reciprocal hreflang tags for any pathname:
 * en / en-cm -> English absolute URL (clean, no query params)
 * fr / fr-cm -> French absolute URL (clean, no query params)
 * x-default -> French absolute URL (clean, no query params, fallback for Cameroon target audience)
 */
export function getHreflangTags(pathname: string): {
  en: string;
  fr: string;
  enCm: string;
  frCm: string;
  xDefault: string;
} {
  const [pathPart] = (pathname || '/').split('#')[0].split('?');
  const clean = pathPart;
  const enPath = getLocalizedEquivalentUrl(clean, 'en');
  const frPath = getLocalizedEquivalentUrl(clean, 'fr');
  const enUrl = `${CANONICAL_DOMAIN}${enPath}`;
  const frUrl = `${CANONICAL_DOMAIN}${frPath}`;
  return {
    en: enUrl,
    fr: frUrl,
    enCm: enUrl,
    frCm: frUrl,
    xDefault: frUrl,
  };
}

/**
 * Detects legacy URLs with query parameters or outdated paths and computes the target redirect URL.
 * Preserves remaining filter query parameters (price, beds, baths, etc.).
 * Safely redirects legacy un-prefixed URLs to the correct preferred localized URL.
 * NEVER redirects admin, auth, or static assets.
 */
export function getLegacyRedirect(
  pathname: string,
  searchParams: URLSearchParams,
  preferredLang?: 'en' | 'fr'
): { destination: string; search: string } | null {
  const path = pathname.toLowerCase().replace(/\/+$/, '');

  // 0. Safety Guard: NEVER redirect admin vaults, authentication flows, or static assets
  if (
    path.startsWith('/admin') ||
    path.startsWith('/auth-action') ||
    path.startsWith('/assets') ||
    path.includes('.')
  ) {
    return null;
  }

  const lang = preferredLang || getCurrentLanguage();
  const remainingParams = new URLSearchParams(searchParams);

  // 0.5 Contradictory or redundant City + Region query handling
  // Handles URLs like /en/land-for-sale/campo?region=centre where a city route has a conflicting ?region=
  const cityRegionMatch = path.match(/^\/(en|fr)\/([a-z0-9-]+)\/([a-z0-9-]+)$/);
  if (cityRegionMatch && remainingParams.has('region')) {
    const routeLang = cityRegionMatch[1];
    const categorySegment = cityRegionMatch[2];
    const citySlug = cityRegionMatch[3];
    const trustedCity = getTrustedCity(citySlug);

    if (trustedCity) {
      const rawReg = remainingParams.get('region') || '';
      const regSlug = slugify(rawReg);
      const normalizedReg = normalizeRegionParam(rawReg);

      if (regSlug === 'all' || normalizedReg === 'All') {
        remainingParams.delete('region');
        return { destination: `/${routeLang}/${categorySegment}/${citySlug}`, search: remainingParams.toString() };
      } else if (trustedCity.region.toLowerCase() !== normalizedReg.toLowerCase()) {
        remainingParams.delete('city');
        remainingParams.delete('location');
        remainingParams.delete('quarter');
        remainingParams.set('region', regSlug);
        return { destination: `/${routeLang}/${categorySegment}`, search: remainingParams.toString() };
      } else {
        remainingParams.delete('region');
        return { destination: `/${routeLang}/${categorySegment}/${citySlug}`, search: remainingParams.toString() };
      }
    }
  }

  // 0.8 Legacy property route patterns (/property/:slug, /properties/:slug, /listing/:slug, /annonce/:slug)
  const legacyPropMatch = path.match(/^\/(?:(en|fr)\/)?(?:listing|annonce|properties)\/([^/?#]+)$/i);
  if (legacyPropMatch) {
    const routeLang = (legacyPropMatch[1] as 'en' | 'fr') || lang;
    const slug = legacyPropMatch[2];
    const destination = routeLang === 'fr' ? `/fr/propriete/${slug}` : `/en/property/${slug}`;
    return { destination, search: remainingParams.toString() };
  }
  const rootPropMatch = path.match(/^\/(?:property|propriete)\/([^/?#]+)$/i);
  if (rootPropMatch) {
    const slug = rootPropMatch[1];
    const destination = lang === 'fr' ? `/fr/propriete/${slug}` : `/en/property/${slug}`;
    return { destination, search: remainingParams.toString() };
  }

  // 0.9 Category page with ?region= query parameter -> 301 redirect to path-based route /:category/:region
  const categoryMatch = path.match(/^\/(?:(en|fr)\/)?([a-z0-9-]+)$/i);
  if (categoryMatch && remainingParams.has('region')) {
    const routeLang = (categoryMatch[1] as 'en' | 'fr') || lang;
    const catSegment = categoryMatch[2];
    const matchedCategory = CATEGORY_ROUTES.find(
      (c) => c.slug === catSegment || c.slugFr === catSegment
    );

    if (matchedCategory) {
      const rawReg = remainingParams.get('region') || '';
      const reg = getRegionBySlug(rawReg);

      if (reg) {
        remainingParams.delete('region');
        const regSlug = routeLang === 'fr' ? reg.slugFr : reg.slug;
        const catSlug = routeLang === 'fr' ? matchedCategory.slugFr : matchedCategory.slug;

        // Check if city query param is also present
        const cityParam = remainingParams.get('city');
        const city = cityParam ? getCityBySlug(cityParam) : null;
        let citySegment = '';
        if (city && isCityInRegion(city.slug, reg.slug)) {
          remainingParams.delete('city');
          citySegment = `/${city.slug}`;
        }

        const destination = `/${routeLang}/${catSlug}/${regSlug}${citySegment}`;
        return { destination, search: remainingParams.toString() };
      } else if (rawReg === 'all' || rawReg === 'All') {
        remainingParams.delete('region');
        const catSlug = routeLang === 'fr' ? matchedCategory.slugFr : matchedCategory.slug;
        const destination = `/${routeLang}/${catSlug}`;
        return { destination, search: remainingParams.toString() };
      }
    }
  }

  // 1. Path-level aliases for legacy query param forms (/buy, /rent, /stay)
  const cat = remainingParams.get('category') || remainingParams.get('type');
  const city = remainingParams.get('city');

  if (
    path === '/buy' ||
    path === '/for-sale' ||
    path === '/listings' ||
    path === '/properties-for-sale' ||
    path === '/en/buy' ||
    path === '/fr/buy'
  ) {
    remainingParams.delete('category');
    remainingParams.delete('type');
    if (city) remainingParams.delete('city');

    let targetBaseEn = 'properties-for-sale';
    let targetBaseFr = 'biens-a-vendre';
    if (cat === 'land') { targetBaseEn = 'land-for-sale'; targetBaseFr = 'terrains-a-vendre'; }
    else if (cat === 'house') { targetBaseEn = 'houses-for-sale'; targetBaseFr = 'maisons-a-vendre'; }
    else if (cat === 'apartment') { targetBaseEn = 'apartments-for-sale'; targetBaseFr = 'appartements-a-vendre'; }
    else if (cat === 'commercial') { targetBaseEn = 'commercial-for-sale'; targetBaseFr = 'commercial-a-vendre'; }

    const citySegment = city ? `/${slugify(city)}` : '';
    const targetBase = lang === 'fr' ? targetBaseFr : targetBaseEn;
    const destination = `/${lang}/${targetBase}${citySegment}`;
    const search = remainingParams.toString();
    if (destination === path && search === searchParams.toString()) {
      return null;
    }
    return { destination, search };
  }

  if (
    path === '/rent' ||
    path === '/rentals' ||
    path === '/properties-for-rent' ||
    path === '/en/rent' ||
    path === '/fr/rent'
  ) {
    remainingParams.delete('category');
    remainingParams.delete('type');
    if (city) remainingParams.delete('city');

    let targetBaseEn = 'properties-for-rent';
    let targetBaseFr = 'biens-a-louer';
    if (cat === 'apartment') { targetBaseEn = 'apartments-for-rent'; targetBaseFr = 'appartements-a-louer'; }
    else if (cat === 'house') { targetBaseEn = 'houses-for-rent'; targetBaseFr = 'maisons-a-louer'; }
    else if (cat === 'studio') { targetBaseEn = 'studios-for-rent'; targetBaseFr = 'studios-a-louer'; }
    else if (cat === 'room') { targetBaseEn = 'rooms-for-rent'; targetBaseFr = 'chambres-a-louer'; }
    else if (cat === 'land') { targetBaseEn = 'land-for-rent'; targetBaseFr = 'terrains-a-louer'; }
    else if (cat === 'commercial') { targetBaseEn = 'commercial-for-rent'; targetBaseFr = 'commercial-a-louer'; }

    const citySegment = city ? `/${slugify(city)}` : '';
    const targetBase = lang === 'fr' ? targetBaseFr : targetBaseEn;
    const destination = `/${lang}/${targetBase}${citySegment}`;
    const search = remainingParams.toString();
    if (destination === path && search === searchParams.toString()) {
      return null;
    }
    return { destination, search };
  }

  if (
    path === '/stay' ||
    path === '/hotel' ||
    path === '/short-stay' ||
    path === '/short-stays' ||
    path === '/hotels' ||
    path === '/en/stay' ||
    path === '/fr/stay'
  ) {
    if (cat || city || path === '/stay' || path === '/hotel' || path === '/short-stay' || (!path.startsWith('/en/') && !path.startsWith('/fr/'))) {
      remainingParams.delete('category');
      remainingParams.delete('type');
      if (city) remainingParams.delete('city');

      let targetBaseEn = 'short-stays';
      let targetBaseFr = 'sejours-courts';
      if (cat === 'hotel' || path === '/hotel' || path === '/hotels') { targetBaseEn = 'hotels'; targetBaseFr = 'hotels'; }
      else if (cat === 'motel') { targetBaseEn = 'motels'; targetBaseFr = 'motels'; }
      else if (cat === 'guesthouse') { targetBaseEn = 'guesthouses'; targetBaseFr = 'maisons-dhotes'; }
      else if (cat === 'resort') { targetBaseEn = 'resorts'; targetBaseFr = 'residences'; }
      else if (cat === 'serviced') { targetBaseEn = 'serviced-apartments'; targetBaseFr = 'appartements-meubles'; }

      const citySegment = city ? `/${slugify(city)}` : '';
      const targetBase = lang === 'fr' ? targetBaseFr : targetBaseEn;
      const destination = `/${lang}/${targetBase}${citySegment}`;
      const search = remainingParams.toString();
      if (destination === path && search === searchParams.toString()) {
        return null;
      }
      return { destination, search };
    }
  }

  // 1.5 Region query parameter resolution on /cities or /villes (e.g. /en/cities?region=Littoral -> /en/regions/littoral)
  if (
    path === '/cities' ||
    path === '/villes' ||
    path === '/en/cities' ||
    path === '/fr/villes' ||
    path === '/cameroon-cities'
  ) {
    if (remainingParams.has('region')) {
      const rawReg = remainingParams.get('region')!;
      remainingParams.delete('region');
      const normalizedReg = normalizeRegionParam(rawReg);
      if (normalizedReg && normalizedReg !== 'All') {
        const regConfig = getRegionBySlug(rawReg) || getRegionBySlug(normalizedReg);
        if (regConfig) {
          const isFrRoute = path.startsWith('/fr') || lang === 'fr';
          const targetSlug = isFrRoute ? regConfig.slugFr : regConfig.slug;
          const destination = isFrRoute ? `/fr/regions/${targetSlug}` : `/en/regions/${targetSlug}`;
          return { destination, search: remainingParams.toString() };
        }
      }
    }
  }

  // 1.6 French path with English slugs (e.g. /fr/land-for-sale/douala -> /fr/terrains-a-vendre/douala)
  if (path.startsWith('/fr/')) {
    const frCanonical = getLocalizedEquivalentUrl(path, 'fr');
    if (frCanonical && frCanonical !== path) {
      return { destination: frCanonical, search: remainingParams.toString() };
    }
  }

  // 1.7 English path with French slugs (e.g. /en/terrains-a-vendre/douala -> /en/land-for-sale/douala)
  if (path.startsWith('/en/')) {
    const enCanonical = getLocalizedEquivalentUrl(path, 'en');
    if (enCanonical && enCanonical !== path) {
      return { destination: enCanonical, search: remainingParams.toString() };
    }
  }

  // 2. Resources tab query param resolution
  if ((path === '/resources' || path === '/en/resources' || path === '/fr/ressources' || path === '/ressources') && remainingParams.has('tab')) {
    const tab = remainingParams.get('tab')!;
    remainingParams.delete('tab');
    const isFrRoute = path.startsWith('/fr') || lang === 'fr';
    const slugMap = isFrRoute ? RESOURCE_TOPIC_SLUGS_FR : RESOURCE_TOPIC_SLUGS;
    const targetTopic = slugMap[tab] || slugify(tab);
    const prefix = isFrRoute ? '/fr/ressources' : '/en/resources';
    return { destination: `${prefix}/${targetTopic}`, search: remainingParams.toString() };
  }

  // 3. Professionals category query param resolution
  if ((path === '/professionals' || path === '/en/professionals' || path === '/fr/professionnels') && remainingParams.has('category')) {
    const proCat = remainingParams.get('category')!;
    remainingParams.delete('category');
    if (proCat !== 'all') {
      const isFrRoute = path.startsWith('/fr') || lang === 'fr';
      const slugMap = isFrRoute ? PROFESSIONAL_SLUGS_FR : PROFESSIONAL_SLUGS;
      const targetCat = slugMap[proCat] || slugify(proCat);
      const prefix = isFrRoute ? '/fr/professionnels' : '/en/professionals';
      return { destination: `${prefix}/${targetCat}`, search: remainingParams.toString() };
    }
  }

  // 4. Unprefixed URLs (e.g. /map, /verify-property, /property/:slug, /land-for-sale)
  // Only redirect legacy unprefixed internal routes; never redirect root '/' or vault/admin routes
  if (path && path !== '/' && !path.startsWith('/en') && !path.startsWith('/fr')) {
    if (
      path.startsWith('/clm-master-vault-9820') ||
      path.startsWith('/admin') ||
      path.startsWith('/__')
    ) {
      return null;
    }
    const localized = getLocalizedEquivalentUrl(path, lang);
    if (localized && localized !== path) {
      return { destination: localized, search: remainingParams.toString() };
    }
  }

  return null;
}
